import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { useClient } from "../../hooks/useClient";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useLogicalSelectedPage, useSelectedSpread } from "../../hooks/useSelection";
import { useSpreadDesign } from "../../hooks/useSpreadDesign";
import { useSpreadDesigns } from "../../hooks/useSpreadDesigns";
import { useTemplate } from "../../hooks/useTemplate";
import { usePageTemplates } from "../../hooks/useTemplates";
import { SpreadDesignId } from "../../types/SpreadDesignId";
import { ILayoutPanelItem, ILayoutsPanelRef, LayoutsPanel } from "./layouts-panel";
import { DragImage, IDragImageRef } from "../drag-image";
import type { Handler } from "@use-gesture/react";
import { getCenterOfElement } from "../../utils/dom";
import { usePreviewContext } from "../../contexts/preview-context";
import { useDashboardContext } from "../../contexts/dashboard-context";

export const LayoutsPanelContainer = () => {
	// Templates
	const { value: templates } = usePageTemplates();
	const [, templateCodeLeftPage, templateCodeRightPage, canApplyTemplate, setTemplateAsync] =
		useTemplate();
	const selectedTemplateIdLeft = templateCodeLeftPage
		? `template:${templateCodeLeftPage}`
		: undefined;
	const selectedTemplateIdRight = templateCodeRightPage
		? `template:${templateCodeRightPage}`
		: undefined;

	// Spread Designs
	const spreadDesigns = useSpreadDesigns();
	const [selectedSpreadDesignId, setSpreadDesign] = useSpreadDesign();

	const client = useClient();
	const { createErrorHandler } = useErrorHandling();

	// Get aspect ratio (needed for templates)
	const page = useLogicalSelectedPage();
	const spread = useSelectedSpread();
	const aspectRatio = page ? page.width / page.height : 1;

	const layoutsPanelRef = useRef<ILayoutsPanelRef>(null);

	const dragImageRef = useRef<IDragImageRef>(null);

	const lastHighlightedZoneRef = useRef<eXpress.core.IZone | undefined>(undefined);
	const { getZonesAtPoint, previewOverlayRef, getCenterOfZone } = usePreviewContext();
	const dashboard = useDashboardContext();

	// effect to focus selected panel only once (when changing spread)
	useEffect(() => {
		const changedId =
			selectedSpreadDesignId || selectedTemplateIdLeft || selectedTemplateIdRight;
		if (layoutsPanelRef.current && changedId) {
			layoutsPanelRef.current.scrollIntoView(changedId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spread]);

	const items = useMemo<readonly ILayoutPanelItem[]>(() => {
		// spread/page designs
		const mappedItems = spreadDesigns
			.map((spreadDesign) => {
				const [left, right] = spreadDesign.pageDesigns;

				const definedAspectRatio = left.aspectRatio || right.aspectRatio;
				const isLeftPage = left?.placement.hasAll(eXpress.creator.LayoutPlacement.Left);
				const isRightPage =
					right?.placement.hasAll(eXpress.creator.LayoutPlacement.Right) ||
					left?.placement.hasAll(eXpress.creator.LayoutPlacement.Right);
				const isSinglePage = left?.placement.hasAll(eXpress.creator.LayoutPlacement.Single);

				const designItem: ILayoutPanelItem = {
					id: spreadDesign.id,
					isSinglePage,
					leftPageThumbUrl: isLeftPage || isSinglePage ? left?.url : undefined,
					leftPageAspectRatio:
						isLeftPage || isSinglePage ? definedAspectRatio : undefined,
					rightPageThumbUrl: isRightPage ? right?.url ?? left?.url : undefined,
					rightPageAspectRatio: isRightPage ? definedAspectRatio : undefined,
				};
				return designItem;
			})
			.distinct((item) => item.id);

		// templates
		const mappedTemplates: ILayoutPanelItem[] = templates
			// sort template by number of photozones
			.orderBy(
				(a, b) => a.landscapeCount + a.portraitCount - (b.landscapeCount + b.portraitCount),
			)
			.map((template) => {
				return {
					id: `template:${template.code}`,
					isSinglePage: true,
					leftPageThumbUrl: template.thumbUrl.getAbsoluteUrl(client),
					leftPageAspectRatio: aspectRatio,
				};
			});

		return [...mappedItems, ...mappedTemplates];
	}, [aspectRatio, client, spreadDesigns, templates]);

	const selectedIds = useMemo(() => {
		// Use memo to pevent creating/passing a new array each time
		const ids = [];
		if (selectedTemplateIdLeft) ids.push(selectedTemplateIdLeft);
		if (selectedTemplateIdRight) ids.push(selectedTemplateIdRight);
		if (selectedSpreadDesignId) ids.push(selectedSpreadDesignId);
		return ids;
	}, [selectedSpreadDesignId, selectedTemplateIdLeft, selectedTemplateIdRight]);

	const onClick = useCallback(
		(id: string) => {
			const colonIndex = id.indexOf(":");
			if (colonIndex < 0) return;

			const type = id.substring(0, colonIndex);
			const value = id.substring(colonIndex + 1);

			if (type === "template") {
				// apply template
				const template = templates.find((temp) => temp.code === value);
				if (template && canApplyTemplate(page)) {
					setTemplateAsync(
						template.code.toLowerCase(),
						template.templateLayoutUrl.getAbsoluteUrl(client),
						page,
					).catch(createErrorHandler());
				}
			} else if (setSpreadDesign && SpreadDesignId.isSpreadDesignId(id)) {
				// Get clicked SpreadDesign
				const selectedSpreadDesign = spreadDesigns.find(
					(spreadDesign) => spreadDesign.id === id,
				);
				if (!selectedSpreadDesign) return;

				// Apply spread design
				setSpreadDesign(selectedSpreadDesign.pageDesigns);
			}
		},
		[
			client,
			createErrorHandler,
			page,
			templates,
			spreadDesigns,
			setSpreadDesign,
			setTemplateAsync,
			canApplyTemplate,
		],
	);

	const onDrag = useCallback<Handler<"drag">>(
		({ last, args, xy: [x, y], axis, tap, dragging, event, first }) => {
			const [layout] = (args as ILayoutPanelItem[]) || [];

			if (!layout) return;

			// ignore first event because we need to wait for a possible tap
			if (first) return;

			// tap happened
			if (tap) {
				onClick(layout.id);
				return;
			}

			const lastHighlightedZone = lastHighlightedZoneRef?.current;

			// regular drag event
			if (axis === "y" && dragging) {
				// drag hasnt been started yet
				if (!dragImageRef?.current?.isDragging()) {
					const dragImageEl = event?.target as unknown as HTMLImageElement;
					let thumbUrl: string | undefined;
					let thumbUrlRight: string | undefined;

					if (layout.isSinglePage) {
						thumbUrl = layout.leftPageThumbUrl;
						if (!dragImageEl.classList.contains("layouts-panel__img")) {
							return;
						}
					} else {
						thumbUrl = layout.leftPageThumbUrl;
						thumbUrlRight = layout.rightPageThumbUrl;
						if (
							!(
								dragImageEl.classList.contains("layouts-panel__img__right") ||
								dragImageEl.classList.contains("layouts-panel__img__left")
							)
						) {
							return;
						}

						// First page spread has only a right page
						if (thumbUrlRight && !thumbUrl) {
							thumbUrl = thumbUrlRight;
							thumbUrlRight = undefined;
						}
					}

					if (thumbUrl) {
						const center = getCenterOfElement(dragImageEl);
						if (center) {
							dragImageRef?.current?.start(
								thumbUrl,
								x,
								y,
								center.left - x,
								center.top - y,
								thumbUrlRight,
							);
						}
					}
				} else {
					// just dragging, move drag
					dragImageRef?.current?.moveTo(x, y);

					const validDropZone = getZonesAtPoint(
						{ x, y },
						(zone) =>
							eXpress.core.isPageZone(zone) && dashboard.canAddTemplateToPage(zone),
					).last() as eXpress.core.PageZone | undefined;

					// highlight target if hovering
					if (validDropZone !== lastHighlightedZone) {
						previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
							highlightZone: false,
						});
						previewOverlayRef.current?.updateZoneState(validDropZone, {
							highlightZone: true,
						});
						lastHighlightedZoneRef.current = validDropZone;
					}
				}
				return;
			}

			// released drag
			if (last) {
				// animation target for after drag (default is the menu item where drag started)
				let target: { top: number; left: number } | undefined = getCenterOfElement(
					event?.target as unknown as HTMLImageElement,
				);

				const currentHoveredPage = eXpress.core.isPageZone(lastHighlightedZoneRef?.current)
					? lastHighlightedZoneRef?.current?.page
					: undefined;

				const colonIndex = layout.id.indexOf(":");
				if (colonIndex < 0) return;

				const type = layout.id.substring(0, colonIndex);
				const value = layout.id.substring(colonIndex + 1);
				let newLayout: eXpress.core.Template | undefined;

				if (type === "template") {
					newLayout = templates.find((temp) => temp.code === value);

					// we have a zone hovered
					if (currentHoveredPage && newLayout && canApplyTemplate(page)) {
						// apply template
						setTemplateAsync(
							newLayout.code.toLowerCase(),
							newLayout.templateLayoutUrl.getAbsoluteUrl(client),
							currentHoveredPage,
						).catch(createErrorHandler());

						target = getCenterOfZone(currentHoveredPage);
					}
				} else if (setSpreadDesign && SpreadDesignId.isSpreadDesignId(layout.id)) {
					// Get clicked SpreadDesign
					const selectedSpreadDesign = spreadDesigns.find(
						(spreadDesign) => spreadDesign.id === layout.id,
					);

					// we have a zone hovered
					if (currentHoveredPage && selectedSpreadDesign) {
						// Apply spread design
						setSpreadDesign(selectedSpreadDesign.pageDesigns);

						target = getCenterOfZone(currentHoveredPage);
					}
				}

				// remove highlight
				previewOverlayRef.current?.updateZoneState(lastHighlightedZone, {
					highlightZone: false,
				});
				lastHighlightedZoneRef.current = undefined;

				// animate to target
				if (target) {
					dragImageRef?.current?.end(target.left - x, target.top - y);
				} else {
					dragImageRef?.current?.end();
				}
			}
		},
		[
			onClick,
			getZonesAtPoint,
			dashboard,
			previewOverlayRef,
			setSpreadDesign,
			templates,
			canApplyTemplate,
			page,
			setTemplateAsync,
			client,
			createErrorHandler,
			getCenterOfZone,
			spreadDesigns,
		],
	);

	return (
		<>
			<DragImage ref={dragImageRef} />
			<LayoutsPanel
				ref={layoutsPanelRef}
				items={items}
				selectedItemIds={selectedIds}
				onClick={onClick}
				onDrag={onDrag}
			/>
		</>
	);
};
